import React, { useState, useEffect } from 'react';
import { useAB } from '@guardian/ab-react';
import { constructQuery } from '@root/src/lib/querystring';
import { incrementUserDismissedGateCount, setUserDismissedGate, } from '@frontend/web/components/SignInGate/dismissGate';
import { getCookie } from '@frontend/web/browser/cookie';
// Sign in Gate A/B Tests
import { signInGateMainVariant } from '@root/src/web/experiments/tests/sign-in-gate-main-variant';
import { signInGateMainControl } from '@root/src/web/experiments/tests/sign-in-gate-main-control';
import { signInGateFakeSocial } from '@root/src/web/experiments/tests/sign-in-gate-fake-social';
// Sign in Gate Types
import { signInGateComponent as gateMainVariant } from '@root/src/web/components/SignInGate/gates/main-variant';
import { signInGateComponent as gateMainControl } from '@root/src/web/components/SignInGate/gates/main-control';
import { signInGateComponent as gateFakeSocialVariant } from '@root/src/web/components/SignInGate/gates/fake-social-variant';
import { submitViewEventTracking, withComponentId, } from './componentEventTracking';
// component name, should always be sign-in-gate
export const componentName = 'sign-in-gate';
const dismissGate = (setShowGate, currentAbTestValue) => {
    setShowGate(false);
    setUserDismissedGate(currentAbTestValue.variant, currentAbTestValue.name);
    incrementUserDismissedGateCount(currentAbTestValue.variant, currentAbTestValue.name);
};
/* When adding a new test, you need to add the test name to the tests array below,
   and add a entry for each variant that maps it to a SignInGateComponent in testVariantToGateMapping, and in turn match each test id to an component id in testIdToComponentId
*/
const tests = [
    signInGateMainVariant,
    signInGateMainControl,
    signInGateFakeSocial,
];
const testVariantToGateMapping = {
    'main-control-4': gateMainControl,
    'main-variant-4': gateMainVariant,
    'fake-social-variant-horizontal': gateFakeSocialVariant,
    'fake-social-variant-vertical': gateFakeSocialVariant,
    'fake-social-control': gateMainVariant,
};
const testIdToComponentId = {
    SignInGateMainVariant: 'main_variant_4',
    SignInGateMainControl: 'main_control_4',
    SignInGateFakeSocial: 'fake_social',
};
// function to generate the profile.theguardian.com url with tracking params
// and the return url (link to current article page)
const generateSignInUrl = (CAPI, currentTest) => {
    // url of the article, return user here after sign in/registration
    const returnUrl = `${CAPI.config.host}/${CAPI.pageId}`;
    // set the component event params to be included in the query
    const queryParams = {
        componentType: 'signingate',
        componentId: testIdToComponentId[currentTest.id],
        abTestName: currentTest.name,
        abTestVariant: currentTest.variant,
        viewId: window.guardian.ophan.viewId,
        browserId: getCookie('bwid') || undefined,
        visitId: getCookie('vsid') || undefined,
    };
    return `${CAPI.config.idUrl}/signin?returnUrl=${returnUrl}&componentEventParams=${encodeURIComponent(constructQuery(queryParams))}`;
};
// component which shows the sign in gate
// fires a VIEW ophan component event
// and show the gate component if it exists
const ShowSignInGate = ({ CAPI, abTest, setShowGate, signInUrl, gateVariant, }) => {
    // use effect hook to fire view event tracking only on initial render
    useEffect(() => {
        submitViewEventTracking({
            component: withComponentId(testIdToComponentId[abTest.id]),
            abTest,
        });
    }, [abTest]);
    // some sign in gate ab test variants may not need to show a gate
    // therefore the gate is optional
    // this is because we want a section of the audience to never see the gate
    // but still fire a view event if they are eligible to see the gate
    if (gateVariant.gate) {
        return gateVariant.gate({
            guUrl: CAPI.config.host || 'https://theguardian.com/',
            signInUrl,
            dismissGate: () => {
                dismissGate(setShowGate, abTest);
            },
            abTest,
            ophanComponentId: testIdToComponentId[abTest.id],
            isComment: CAPI.format.design === 'CommentDesign' ||
                CAPI.format.design === 'EditorialDesign',
        });
    }
    // return nothing if no gate needs to be shown
    return React.createElement(React.Fragment, null);
};
// component with conditional logic which determines if a sign in gate
// should be shown on the current page
export const SignInGateSelector = ({ isSignedIn, CAPI, }) => {
    const [showGate, setShowGate] = useState(true);
    const [currentTest, setCurrentTest] = useState({
        name: '',
        variant: '',
        id: '',
    });
    const ab = useAB();
    useEffect(() => {
        const test = ab.firstRunnableTest(tests);
        setCurrentTest({
            name: test?.dataLinkNames || test?.id || '',
            variant: test?.variantToRun.id || '',
            id: test?.id || '',
        });
    }, [ab]);
    useEffect(() => {
        // this hook will fire when the sign in gate is dismissed
        // which will happen when the showGate state is set to false
        // this only happens within the dismissGate method
        if (showGate === false) {
            document.dispatchEvent(new CustomEvent('dcr:page:article:redisplayed'));
        }
    }, [showGate]);
    // check to see if the test is available on this render cycle
    // required by the ab test framework, as we have to wait for the above
    // useEffect hook to determine which test to run
    if (currentTest.name === '' || currentTest.variant === '') {
        return null;
    }
    const signInUrl = generateSignInUrl(CAPI, currentTest);
    const gateVariant = testVariantToGateMapping?.[currentTest.variant];
    return (React.createElement(React.Fragment, null, showGate &&
        gateVariant?.canShow(CAPI, !!isSignedIn, currentTest) && (React.createElement(ShowSignInGate, { CAPI: CAPI, abTest: currentTest, setShowGate: setShowGate, signInUrl: signInUrl, gateVariant: gateVariant }))));
};
